import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, IconButton, Typography, Alert, CircularProgress, List, ListItem, ListItemText, Link
} from '@mui/material';
import { Close as CloseIcon, Upload as UploadIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { skillService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import axios from 'axios';
import { styled } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';

const DropzoneArea = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: '12px',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.grey[50],
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        backgroundColor: theme.palette.grey[100]
    }
}));

const ImportModal = ({ open, onClose, importType, onSuccess }) => {
    const { showToast } = useToast();
     const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [downloadLoading, setDownloadLoading] = useState(false);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'text/csv': ['.csv']
        },
        multiple: false,
        onDrop: (acceptedFiles, fileRejections) => {
            if (fileRejections.length > 0) {
                setError('Please upload a valid CSV file only.');
                return;
            }

            const file = acceptedFiles[0];
            if (file) {
                setFile(file);
                setFileName(file.name);
                setError('');
            }
        }
    });

    const downloadSampleCsv = async () => {
        try {
            setDownloadLoading(true);
            
            let csvContent, filename;
            
            if (importType === 'categories') {
                csvContent = 'category_name,description\nJavaScript,Programming language skills\nProject Management,Management and leadership skills\nData Science,Data analysis and machine learning skills';
                filename = 'sample_categories_import.csv';
            } else {
                csvContent = 'skill_name,description,category_name\nReact,Frontend framework,JavaScript\nNode.js,Backend runtime,JavaScript\nPython,Programming language,Data Science';
                filename = 'sample_skills_import.csv';
            }
            
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);

            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            setDownloadLoading(false);
        } catch (error) {
            console.error('Download error:', error);
            showToast('Error downloading sample file. Please try again.', 'error');
            setDownloadLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!file) {
            setError('Please select a CSV file');
            return;
        }

        try {
            setLoading(true);
            setError('');

            const formData = new FormData();
            formData.append('csvFile', file);

            let response;
            if (importType === 'categories') {
                response = await skillService.importCategories(formData);
            } else {
                response = await skillService.importSkills(formData);
            }

            if (response.data.status === 'ok') {
                setResult(response.data);
                showToast(response.data.message, 'success');
                if (onSuccess) onSuccess();
            }
        } catch (error) {
            console.error('Error importing:', error);
            setError(error.response?.data?.message || `Failed to import ${importType}`);
        } finally {
            setLoading(false);
        }
    };   

    const handleClose = () => {
        setFile(null);
        setFileName('');
        setError('');
        setResult(null);
        onClose();
    };

    return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        Import {importType === 'categories' ? 'Categories' : 'Skills'}
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent dividers>
                    {result && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <Typography variant="body1" gutterBottom>
                                {result.message}
                            </Typography>
                            {result.details.errors.length > 0 && (
                                <>
                                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                        Errors:
                                    </Typography>
                                    <List dense sx={{ maxHeight: '150px', overflow: 'auto' }}>
                                        {result.details.errors.map((error, index) => (
                                            <ListItem key={index}>
                                                <ListItemText primary={error} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                        </Alert>
                    )}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            {importType === 'categories' 
                                ? 'Required columns: category_name, description (optional)'
                                : 'Required columns: skill_name, category_name, description (optional)'
                            }
                        </Typography>
                    </Box>

                    <DropzoneArea {...getRootProps()}>
                        <input {...getInputProps({ accept: '.csv' })} />
                        <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            {isDragActive ? 'Drop files here' : 'Drag and drop files here'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            or click to browse
                        </Typography>
                        {fileName && (
                            <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                                Selected: {fileName}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                            Supported formats: .csv
                        </Typography>
                    </DropzoneArea>

                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                        {downloadLoading ? (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                        ) : (
                            <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} color="primary" />
                        )}
                        <Link
                            component="button"
                            variant="body2"
                            onClick={downloadSampleCsv}
                            underline="hover"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                            type="button"
                        >
                            Download Sample CSV
                        </Link>
                    </Box>

                    {loading && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            <Typography variant="body2">
                                Importing...
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={loading}>
                        {result ? 'Close' : 'Cancel'}
                    </Button>
                    {!result && (
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={loading || !file}
                            startIcon={loading && <CircularProgress size={20} />}
                        >
                            Import
                        </Button>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ImportModal;